import { apiClient } from '../utils/api';

export interface DocumentTemplateDto {
  id: string;
  code: string;
  version: number;
  description?: string | null;
  language: string;
  filePath: string;
  isActive: boolean;
  isDefaultPiercing: boolean;
  isDefaultPiercingMinor: boolean;
  isDefaultTattoo: boolean;
  requiredSigners: string[];
  /** 'LAST_PAGE' = append a signature page (default) | 'INLINE' = draw at {{SIGN:ROLE}} placeholder */
  signaturePlacement: 'LAST_PAGE' | 'INLINE';
  createdAt: string;
  updatedAt: string;
  fileSize?: number | null;
  fileMtime?: string | null;
}

export interface UploadTemplateResponse {
  success: boolean;
  filePath: string;
  originalName: string;
  size: number;
}

export const listDocumentTemplates = async (): Promise<DocumentTemplateDto[]> => {
  const res = await apiClient('/api/document-templates');
  if (!res.ok) throw new Error('Failed to load templates');
  return res.json();
};

export const uploadDocumentTemplate = async (params: {
  file: File;
  code?: string;
  language?: string;
  version?: number;
}): Promise<UploadTemplateResponse> => {
  const formData = new FormData();
  formData.append('file', params.file);
  if (params.code) formData.append('code', params.code);
  if (params.language) formData.append('language', params.language);
  if (typeof params.version !== 'undefined') {
    formData.append('version', String(params.version));
  }

  const res = await apiClient('/api/document-templates/upload', {
    method: 'POST',
    body: formData,
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || 'Upload failed');
  }
  return res.json();
};

export const registerDocumentTemplate = async (payload: {
  code: string;
  version: number;
  description?: string;
  language: string;
  filePath: string;
  isActive?: boolean;
}): Promise<DocumentTemplateDto> => {
  const res = await apiClient('/api/document-templates', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error('Failed to register template');
  return res.json();
};

export const updateDocumentTemplate = async (
  id: string,
  payload: Partial<Pick<DocumentTemplateDto, 'description' | 'isActive' | 'isDefaultPiercing' | 'isDefaultPiercingMinor' | 'isDefaultTattoo' | 'requiredSigners' | 'signaturePlacement'>>,
): Promise<DocumentTemplateDto> => {
  const res = await apiClient(`/api/document-templates/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error('Failed to update template');
  return res.json();
};

export const deleteDocumentTemplate = async (id: string): Promise<void> => {
  const res = await apiClient(`/api/document-templates/${id}`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || 'Failed to delete template');
  }
};

export const replaceDocumentTemplateFile = async (id: string, file: File): Promise<DocumentTemplateDto> => {
  const formData = new FormData();
  formData.append('file', file);
  const res = await apiClient(`/api/document-templates/${id}/file`, {
    method: 'PUT',
    body: formData,
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || 'Failed to replace template file');
  }
  return res.json();
};

